import lodash from "lodash";
import { defineAsyncComponent, reactive } from "vue";
import store from "../../../../store";
import { createLoading } from "../../../../utils/loading";
import { have } from "../../../../utils/menu-context";
import { createManageExportCsv, createPage, onClickReport } from "../../../../utils/page";
import { flatWhereGroup } from "../../../../utils/search";
import { callFeeStatus } from "../../fee/fee/fee.logic";
import { createColumns } from "./fee.section";
import { createButton } from "../../../../utils/button";
import i18n from "../../../../i18n";
import moment from "moment";

export const { isLoading, setIsLoading } = createLoading();

const CIcon = defineAsyncComponent(() => import("../../../../components/icon/CIcon"));

export const actionPrefix = "resource/v1/transaction/feeStatement";
export const actionPrefixLookup = "resource/v1/master/lookup";

export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callSearchLookup = ({ criteria }) => store.dispatch(`${actionPrefixLookup}/search`, { criteria });
export const callReportFeeStatementExcel = ({ data }) => store.dispatch(`${actionPrefix}/reportFeeStatementExcel`, { data, config: {responseType: 'blob'} });
export const callReportPaymentSummaryExcel = ({ data }) => store.dispatch(`${actionPrefix}/reportPaymentSummaryExcel`, { data, config: {responseType: 'blob'} });

export const onSearch = ({ props, page, isSet = true }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    const customWhere = lodash.cloneDeep(where);
    const companyNameThIndex = customWhere.findIndex(d => d.name === "refUserCompany.companyNameTh");
    if (companyNameThIndex !== -1) {
        const companyNameThWhere = customWhere[companyNameThIndex];
        customWhere.splice(
            companyNameThIndex,
            1,
            {
                openBucket: true,
                name: "refUserCompany.companyNameTh",
                type: "String",
                operator: "like",
                value: companyNameThWhere.value
            },
            {
                logic: "OR",
                closeBucket: true,
                name: "refUserCompany.fullNameTh",
                type: "String",
                operator: "like",
                value: companyNameThWhere.value
            }
        );
    }
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({
        criteria: {
            ...page,
            data: [],
            where: [...customWhere, ...createWhereFor({ props })],
            sort
        }
    })
        .then(page => (isSet ? (state.page = { ...page, where, sort }) : page))
        .finally(() => setIsLoading(false));
};

export const onSearchLookup = () => {
    setIsLoading(true);
    return callSearchLookup({
        criteria: {
            select: ["*"],
            where: [
                { name: "group", type: "String", operator: "=", value: "REPORT" },
                { name: "key", type: "String", operator: "=", value: "fee" }
            ]
        }
    })
        .then(page => (state.lookupReport = page.data?.[0]))
        .finally(() => setIsLoading(false));
};

export const createFormTitle = () => {
    return state.lookupReport?.value;
};

export const createWhereFor = ({ props }) => {
    const { menuContext } = props;
    const whereGroup = [];
    const userInfo = store.state.resource.auth.info?.user;
    const departmentId = userInfo.refDepartment?.id;
    if (have({ menuContext, keys: ["customs", "province"] })) {
        whereGroup.push([
            { name: "refUserCompany.refDepartment.id", type: "UUID", operator: "=", value: departmentId },
            { logic: "OR", name: "refUserCompany.refDepartment.refParent.id", type: "UUID", operator: "=", value: departmentId },
            { logic: "OR", name: "refUserCompany.refDepartment.refParent.refParent.id", type: "UUID", operator: "=", value: departmentId },
            { logic: "OR", name: "refUserCompany.refDepartment.refParent.refParent.refParent.id", type: "UUID", operator: "=", value: departmentId },

            { logic: "OR", name: "refUserCompany.refDepartmentRegister.id", type: "UUID", operator: "=", value: departmentId },
            { logic: "OR", name: "refUserCompany.refDepartmentRegister.refParent.id", type: "UUID", operator: "=", value: departmentId },
            { logic: "OR", name: "refUserCompany.refDepartmentRegister.refParent.refParent.id", type: "UUID", operator: "=", value: departmentId },
            {
                logic: "OR",
                name: "refUserCompany.refDepartmentRegister.refParent.refParent.refParent.id",
                type: "UUID",
                operator: "=",
                value: departmentId
            }
        ]);
    }
    return [...flatWhereGroup({ where: whereGroup, logic: "OR" })];
};

export const initState = ({ props }) => {
    const { menuContext } = props;
    state.manages = [
        createManageExportCsv({
            menuContext,
            callData: ({ page }) => onSearch({ props, page: { ...lodash.cloneDeep({ ...page, page: 0, size: 1000000 }) }, isSet: false })
        })
    ];
    state.manageExtras = [
        {
            visible: () => true,
            render: () => createButton({
                text: `${i18n.global.t("views.fee.reportExcel")}`,
                classStyle: `m-1 btn-primary`,
                onClick: () => onClickReport({
                    type: "application/xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    fileName: `รายงานการรับชำระค่าธรรมเนียม_${moment().format("YYYYMMDDHHmmss")}.xlsx`,
                    callReport: () => callReportFeeStatementExcel({ data: state?.page || {} }),
                }),
                icon: <CIcon file="printer.svg"></CIcon>
            })
        },
        {
            visible: () => true,
            render: () => createButton({
                text: `${i18n.global.t("views.fee.reportPaymentSummary")}`,
                classStyle: `m-1 btn-primary`,
                onClick: () => onClickReport({
                    type: "application/xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    fileName: `รายงานสรุปการรับชำระค่าธรรมเนียม_${moment().format("YYYYMMDDHHmmss")}.xlsx`,
                    callReport: () => callReportPaymentSummaryExcel({ data: state?.page || {} }),
                }),
                icon: <CIcon file="printer.svg"></CIcon>
            })
        },
    ];
    state.rowActions = [];
    onSearchLookup();
    callFeeStatus({}).then(feeStatuses => {
        state.feeStatuses = feeStatuses;
        if (!state.columns) state.columns = createColumns({ state });
        state.isLoaded = true;
    });
};

export const state = reactive({
    columns: null,
    page: createPage({
        options: {
            select: [
                "*",
                "refUserCompany.*",
                "refUserCompany.refPersonType.*",
                "refStatus.*",
                "refType.*",
                "refRequest.*",
                "refOfficer.*",
                "refRequest.refDepartment.*",
                "refRequest.refRequestDoc.*"
            ],
            sort: [{ name: "payDatetime", type: "DESC" }]
        }
    }),
    tableOptions: { search: { activeKey: "Search" } },
    lookupReport: null,
    feeStatuses: [],
    isLoaded: false
});
